import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import Text from "components/atoms/Text";
import Input from "components/atoms/Input";
import Icon from "components/atoms/Icon";
import Image from "components/atoms/Image";
import Button from "components/atoms/Button";
import { COLOR_SYSTEM } from "components/atoms";
import {
  fetchUserDrugIdentificationList,
  DrugSearchResultItem
} from "api/drugIdentificationService";
import { DrugIdentification } from "common/models/types";
import { log, LogLevel } from "utils/LogUtil";

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const DrugListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  max-height: 600px;
`;

const DrugCard = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${COLOR_SYSTEM.get("Blue")?.[300]};
    background: ${COLOR_SYSTEM.get("Blue")?.[25]};
  }
`;

const DrugImageContainer = styled.div`
  flex-shrink: 0;
`;

const DrugInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const DrugName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DrugMeta = styled.div`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

interface IdentifiedDrugListProps {
  onDrugSelect: (selectedItemCode: string) => void;
  onClose: () => void;
}

const IdentifiedDrugList: React.FC<IdentifiedDrugListProps> = ({ onDrugSelect, onClose }) => {
  const [identifiedDrugs, setIdentifiedDrugs] = useState<DrugIdentification[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 20;

  // 초기 데이터 로드
  useEffect(() => {
    const loadIdentifiedDrugs = async () => {
      try {
        setLoading(true);
        const response = await fetchUserDrugIdentificationList({
          status: "selected",
          limit: LIMIT,
          offset: 0,
        });

        setIdentifiedDrugs(response.data || []);
        setHasMore(response.meta?.hasMore || false);
        setOffset(LIMIT);
        setError(null);
      } catch (err) {
        log(LogLevel.ERROR, "[IdentifiedDrugList] 식별약 목록 조회 실패:", err);
        setError("식별약 목록을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    loadIdentifiedDrugs();
  }, []);

  // 1. 중복 제거 (동일 itemCode는 하나만)
  const uniqueDrugs = useMemo(() => {
    const seen = new Set<string>();
    return identifiedDrugs.filter(drug => {
      if (!drug.selectedItemCode || seen.has(drug.selectedItemCode)) return false;
      seen.add(drug.selectedItemCode);
      return true;
    });
  }, [identifiedDrugs]);

  // 2. 선택된 약품 정보 추출 (results 배열에서 selectedItemCode에 해당하는 약품 찾기)
  const drugsWithDetails = useMemo(() => {
    return uniqueDrugs.map(identification => {
      const selectedDrug = identification.results?.find(
        r => r.itemSeq === identification.selectedItemCode
      );
      return { identification, selectedDrug };
    }).filter(item => item.selectedDrug); // 약품 정보가 있는 것만
  }, [uniqueDrugs]);

  // 3. 클라이언트 사이드 필터링
  const filteredDrugs = useMemo(() => {
    if (!searchQuery.trim()) return drugsWithDetails;

    const query = searchQuery.toLowerCase();
    return drugsWithDetails.filter(({ selectedDrug }) =>
      selectedDrug?.itemName?.toLowerCase().includes(query) ||
      selectedDrug?.entpName?.toLowerCase().includes(query)
    );
  }, [drugsWithDetails, searchQuery]);

  const handleDrugClick = (itemCode: string | undefined) => {
    if (!itemCode) return;
    onDrugSelect(itemCode);
  };

  const closeButton = (
    <Button
      buttonType="button"
      color="Secondary"
      size="Small"
      type="Icon"
      variant="Ghost"
      icon="Chevron Left"
      onClick={onClose}
      title="패널 닫기"
    />
  );

  if (loading) {
    return (
      <PanelContainer>
        <PanelHeader>
          <Text textType="Body1">내가 식별한 약</Text>
          {closeButton}
        </PanelHeader>
        <LoadingState>
          <Text textType="Caption">불러오는 중...</Text>
        </LoadingState>
      </PanelContainer>
    );
  }

  if (error) {
    return (
      <PanelContainer>
        <PanelHeader>
          <Text textType="Body1">내가 식별한 약</Text>
          {closeButton}
        </PanelHeader>
        <EmptyState>
          <Icon name="Exclamation Mark Circle" size={32} color={COLOR_SYSTEM.get("Red")?.[500]} />
          <Text textType="Caption" style={{ marginTop: "8px" }}>
            {error}
          </Text>
        </EmptyState>
      </PanelContainer>
    );
  }

  return (
    <PanelContainer>
      <PanelHeader>
        <Text textType="Body1">내가 식별한 약</Text>
        {closeButton}
      </PanelHeader>

      <SearchContainer>
        <Input
          placeholder="약품명 검색..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leftItems={[<Icon key="search" name="Search" size={16} color={COLOR_SYSTEM.get("Gray")?.[400]} />]}
        />
      </SearchContainer>

      {filteredDrugs.length === 0 ? (
        <EmptyState>
          {searchQuery ? (
            <>
              <Icon name="Search" size={32} color={COLOR_SYSTEM.get("Gray")?.[400]} />
              <Text textType="Caption" style={{ marginTop: "8px" }}>
                검색 결과가 없습니다.
              </Text>
            </>
          ) : (
            <>
              <Icon name="Pills" size={32} color={COLOR_SYSTEM.get("Gray")?.[400]} />
              <Text textType="Caption" style={{ marginTop: "8px" }}>
                확정된 식별약이 없습니다.
              </Text>
            </>
          )}
        </EmptyState>
      ) : (
        <DrugListContainer>
          {filteredDrugs.map(({ identification, selectedDrug }) => (
            <DrugCard
              key={identification.id}
              onClick={() => handleDrugClick(identification.selectedItemCode)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleDrugClick(identification.selectedItemCode);
                }
              }}
            >
              <DrugImageContainer>
                <Image
                  src={selectedDrug?.itemImage || "/images/pill-placeholder.png"}
                  alt={selectedDrug?.itemName || "약품"}
                  width={80}
                  height={50}
                  objectFit="cover"
                  enableHoverZoom
                  zoomScale={4}
                  zoomSide="left"
                  showOnError
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/images/pill-placeholder.png";
                  }}
                />
              </DrugImageContainer>
              <DrugInfo>
                <DrugName>{selectedDrug?.itemName}</DrugName>
                <DrugMeta>
                  {[
                    selectedDrug?.entpName,
                    selectedDrug?.formulation,
                    selectedDrug?.shape
                  ].filter(Boolean).join(" | ")}
                </DrugMeta>
              </DrugInfo>
            </DrugCard>
          ))}
        </DrugListContainer>
      )}
    </PanelContainer>
  );
};

export default IdentifiedDrugList;
